import type { MaybeRefOrGetter, Ref } from "vue";
import { ref, watch, toValue } from "vue";
import { Form } from "@king-design/vue";
import { useDebounceFn } from "@vueuse/core";

/** @param fn return true if duplicate, otherwise return false */
export function useCheckDupString(
  str: Ref<string>,
  fn: (v: string) => Promise<boolean>,
  debounceMs: MaybeRefOrGetter<number> = 200,
) {
  const isDup = ref(false);

  // 虽然使用了 debounce，可以减少请求，但是无法完全防止 ABBA 问题，所以需要使用 seq 来保证请求的顺序
  // 没有使用 lastValue 的原因是，当请求了两次 lastValue 时，得到的结果可能不一致，同样可能遇到 ABBA 的问题。
  const context = {
    seq: 0,
  };

  function getCurrentSeq() {
    return context.seq;
  }

  function consumeCurrentSeq() {
    return context.seq++;
  }

  const isChecking = ref(false);

  const check = useDebounceFn(async (v: string) => {
    isChecking.value = true;
    const seq = consumeCurrentSeq();
    const result = await fn(v);
    if (seq + 1 === getCurrentSeq()) {
      isChecking.value = false;
      isDup.value = result;
    }
    return isDup.value;
  }, toValue(debounceMs));

  watch(str, newValue => {
    check(newValue);
  });

  return {
    isDup,
    isChecking,
    check,
  };
}

export function registerCheckDupStringFormRule() {
  Form.addMethod(
    "is-dup-string",
    (_v: string, params: { is: boolean; message?: string }) => {
      const isDup = params.is;
      return isDup ? params.message ?? false : true;
    },
    "字符串重复",
  );
}
